import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Autocomplete, Badge, Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItemButton, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { authRequest, getToken } from "../services/auth";
import { ErrorState, LoadingState, RefreshButton } from "./AsyncViewState";

const messagesApi = () => axios.create({ baseURL: `${process.env.REACT_APP_API_BASE_URL}/api/messages`, headers: { Authorization: `Bearer ${getToken()}` } });

function MessageList({ messages, mode, directory, expandedId, onToggle }) {
  if (!messages.length) return <Box sx={{ py: 12, textAlign: "center" }}><Typography color="text.secondary">No active {mode === "sent" ? "sent " : ""}messages.</Typography></Box>;
  const names = new Map(directory.map((user) => [user.userId, user.name || user.userId]));

  return <List disablePadding>{messages.map((message, index) => {
    const rowId = `${mode}-${message.id}`;
    const expanded = expandedId === rowId;
    const recipients = (message.recipientIds || []).map((id) => names.has(id) ? `${names.get(id)} (${id})` : id).join(", ");
    const heading = mode === "sent" ? `To: ${recipients}` : message.senderName;
    return <React.Fragment key={rowId}>
      <ListItemButton alignItems="flex-start" onClick={() => onToggle(expanded ? "" : rowId)} aria-expanded={expanded} sx={{ px: 3, py: 1.75, transition: "background-color 160ms ease" }}>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontWeight: mode === "inbox" && !message.readAt ? 850 : 700 }}>{heading}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{new Date(message.createdAt).toLocaleString()}</Typography>
            <ExpandMoreRoundedIcon sx={{ color: "text.secondary", transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 180ms ease" }} />
          </Stack>
          {!expanded && <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: .6 }}>{message.body}</Typography>}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.25, p: 1.5, borderRadius: 2, bgcolor: "rgba(20,109,155,.055)", border: "1px solid rgba(20,109,155,.1)" }}>
              <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{message.body}</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={.5} sx={{ mt: 1.25 }}>
                <Typography variant="caption" color="text.secondary">Expires {new Date(message.expiresAt).toLocaleString()}</Typography>
                {mode === "sent" && <Typography variant="caption" color="text.secondary">Read by {message.readCount} of {message.recipientCount}</Typography>}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      </ListItemButton>
      {index < messages.length - 1 && <Divider />}
    </React.Fragment>;
  })}</List>;
}

export default function MessagesPanel({ open, onClose, onUnreadChange }) {
  const [tab, setTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [expandedId, setExpandedId] = useState("");
  const [directory, setDirectory] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [{ data: inbox }, { data: sent }, { data: users }] = await Promise.all([messagesApi().get("/"), messagesApi().get("/sent"), authRequest().get("/directory")]);
      const readAt = new Date().toISOString();
      if (inbox.some((message) => !message.readAt)) await messagesApi().post("/read-all");
      setMessages(inbox.map((message) => ({ ...message, readAt: message.readAt || readAt })));
      setSentMessages(sent); setDirectory(users);
      onUnreadChange?.(0);
    } catch (loadError) { setError(loadError.response?.data?.error || "An error occurred while loading messages"); }
    finally { setLoading(false); }
  }, [onUnreadChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async () => {
    if (!recipients.length || !body.trim()) return;
    setSending(true); setError(""); setSuccess("");
    try {
      const { data } = await messagesApi().post("/", { recipients: recipients.map((user) => user.userId), body: body.trim() });
      setSuccess(data.message); setRecipients([]); setBody(""); setTab(1); await load();
    } catch (sendError) { setError(sendError.response?.data?.error || "An error occurred while sending the message"); }
    finally { setSending(false); }
  };

  const remaining = useMemo(() => 300 - body.length, [body]);
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle sx={{ pb: 0 }}><Stack direction="row" alignItems="center" justifyContent="space-between"><Box><Typography variant="h6" sx={{ fontWeight: 800 }}>Messages</Typography><Typography variant="caption" color="text.secondary">Direct messages expire after 72 hours.</Typography></Box><RefreshButton onClick={load} loading={loading} label="Refresh messages" /></Stack></DialogTitle>
    <Tabs value={tab} onChange={(_, value) => { setTab(value); setExpandedId(""); }} sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}><Tab label={<Badge color="primary" badgeContent={messages.filter((message) => !message.readAt).length}>Inbox</Badge>} /><Tab label="Sent" /><Tab label="New message" /></Tabs>
    <DialogContent sx={{ minHeight: 410, p: 0 }}>
      {error && <Alert severity="error" sx={{ m: 2 }}>{error} <Button size="small" onClick={load}>Retry</Button></Alert>}
      {success && <Alert severity="success" sx={{ m: 2 }}>{success}</Alert>}
      {loading ? <LoadingState label="Loading messages..." /> : tab === 0
        ? (error && !messages.length ? <ErrorState onRetry={load} message={error} /> : <MessageList messages={messages} mode="inbox" directory={directory} expandedId={expandedId} onToggle={setExpandedId} />)
        : tab === 1
          ? (error && !sentMessages.length ? <ErrorState onRetry={load} message={error} /> : <MessageList messages={sentMessages} mode="sent" directory={directory} expandedId={expandedId} onToggle={setExpandedId} />)
          : <Stack spacing={2.25} sx={{ p: 3 }}>
            <Autocomplete multiple options={directory} value={recipients} onChange={(_, value) => setRecipients(value)} getOptionLabel={(option) => `${option.name} (${option.userId})`} isOptionEqualToValue={(option, value) => option.userId === value.userId} renderInput={(params) => <TextField {...params} label="Recipients" placeholder="Select users" />} />
            <TextField label="Message" value={body} onChange={(event) => setBody(event.target.value.slice(0, 300))} multiline minRows={5} helperText={`${remaining} characters remaining`} inputProps={{ maxLength: 300 }} />
            <Alert severity="info">This message will be available to recipients and in your Sent view for a maximum of 72 hours.</Alert>
            <Box><Button variant="contained" startIcon={<SendRoundedIcon />} onClick={send} disabled={sending || !recipients.length || !body.trim()} sx={{ textTransform: "none" }}>{sending ? "Sending..." : "Send message"}</Button></Box>
          </Stack>}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
}
